/**
 * Inspired by the Material Design Input:
 * https://github.com/angular/components/blob/master/src/material/input/input.ts
 */
import { Directive, HostBinding, HostListener, inject } from '@angular/core';
import { NgControl } from '@angular/forms';
import { isControlValid } from '../utils';
import { MuiInputNativeDirective } from './mui-input-native.directive';


@Directive()
export abstract class MuiInputBaseDirective<T = any> extends MuiInputNativeDirective {
  get value(): T {
    return this.ngControl ? this.ngControl.value : this._elementRef.nativeElement.value;
  }

  get valid(): boolean {
    return this.ngControl ? isControlValid(this.ngControl) : this._elementRef.nativeElement.checkValidity();
  }

  get hasError(): boolean {
    return this.invalid;
  }

  @HostBinding('class.has--value')
  get hasValue(): boolean {
    return !!this.value;
  }

  get tag(): string {
    const regexRes = /<(\w+)[\s>]/.exec(this._elementRef.nativeElement.outerHTML) ?? '';
    return regexRes.length > 1 ? regexRes[1] : this._elementRef.nativeElement.tagName;
  }

  ngControl: NgControl | null = inject(NgControl, { optional: true, self: true });
  abstract type: string | null;
  protected abstract _uid: string;
  protected _initialValue: T | string = '';

  @HostListener('keydown.escape', ['$event'])
  resetToInitialValue(event: Event): void {
    event.preventDefault();

    // If an NgControl and an initial value exists, reset the control to the initial value.
    if (this.ngControl && this._initialValue && this._initialValue !== this.ngControl.value) {
      this.ngControl.reset(this._initialValue);
    }

    // If no NgControl is present, fallback to reset the native HTML input element.
    if (
      !this.ngControl &&
      this._initialValue &&
      typeof this._initialValue === 'string' &&
      this._initialValue !== this._elementRef.nativeElement.value
    ) {
      this._elementRef.nativeElement.value = this._initialValue;
    }

    // Remove focus
    this.blur();
  }

  @HostListener('focus')
  protected onFocus(): void {
    this._initialValue = this.ngControl ? this.ngControl.value : this._elementRef.nativeElement.value;
  }
}
